import React, { useEffect, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useCalendar } from 'components/providers/CalendarProvider'
import {
  AccountDoc,
  EventDoc,
  GroupDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { buildEventsArrayForUIFromDBEventsWithRecurrentEvents } from 'utils/api/events'
import { isEventWithinTimePeriod } from './CalendarView'
import { isEventRecurrenceValid } from 'utils/event'
import { defaultGroupImage } from 'utils/api/groups'
import { provideEventTitle } from 'utils/event'
import { getSupportedTimezone } from 'utils/event'
import { getDefaultTimezone } from 'index'
import dateToTimeString from './components'
import { theme } from 'theme'
import { useEventPopover } from './eventPopover/EventPopoverProvider'
import { EventPopupContent } from 'components/popups/eventInfo/EventPopup'
import { isTrulyEmpty } from 'utils/object'
import { eventIdForContainer } from 'utils/event'
import { isGuest } from 'utils/user'
import { LocationIcon } from 'assets/icons'
import EventCoverImage, {
  getCoverImageNote
} from 'components/popups/eventInfo/EventCoverImage'
import { t } from 'i18next'
import Logger from 'services/Logger'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import Database from 'services/db/Database'

const CARD_WIDTH = 230
const CARD_HEIGHT = 290
const MIN_GAP = 16
const IMAGE_HEIGHT = 170

interface GalleryViewProps {
  events: Model<EventDoc>[] | EventDoc[]
  userSettings?: UserSettingsDoc
  account?: AccountDoc
  group?: Model<GroupDoc>
  groupId?: string
  groups?: GroupDoc[]
  embedMode?: boolean
  loadingEvents?: boolean
  showGroupTag: boolean
}

export function calculateGalleryViewRange(dateStr: string) {
  const parsedDate = dayjs(dateStr)
  const anchor = parsedDate.isValid() ? parsedDate : dayjs()
  const start = anchor.startOf('month').startOf('day')
  const end = anchor.endOf('month').endOf('day')
  return { start, end }
}

const GalleryView: React.FC<GalleryViewProps> = React.memo(
  (props: GalleryViewProps) => {
    const calendar = useCalendar()

    const [eventsForGallery, setEventsForGallery] = React.useState<EventDoc[]>(
      []
    )
    const [isProcessingEvents, setIsProcessingEvents] = React.useState(false)

    const { visibleAreaStart, visibleAreaEnd } = useMemo(() => {
      const visibleDate = LocalStorage.get(LocalStorageKey.VISIBLE_DATE) ?? ''
      const anchorDate =
        calendar.currentMonth || visibleDate || dayjs().toISOString()
      const range = calculateGalleryViewRange(anchorDate)
      return { visibleAreaStart: range.start, visibleAreaEnd: range.end }
    }, [calendar.currentMonth, calendar.view])

    const filteredEvents = useMemo(() => {
      return props.events.filter((event) => {
        if (
          !props.showGroupTag &&
          event.GroupID !== props.groupId &&
          !(event.SupplementaryGroupsIDs ?? []).includes(props.groupId ?? '')
        ) {
          return false
        }

        return (
          isEventWithinTimePeriod(event, visibleAreaStart, visibleAreaEnd) ||
          isEventRecurrenceValid(event, visibleAreaStart)
        )
      })
    }, [
      props.events,
      visibleAreaStart,
      visibleAreaEnd,
      props.showGroupTag,
      props.groupId
    ])

    useEffect(() => {
      let isMounted = true
      setIsProcessingEvents(true)

      const events = buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
        filteredEvents,
        visibleAreaStart,
        visibleAreaEnd
      )
      const groupCache = new Map<string, GroupDoc | null>()

      Promise.all(
        events.map(async (event) => {
          const groupId = event.GroupID ?? ''
          if (!groupCache.has(groupId)) {
            const group = await localGroupById(groupId)
            groupCache.set(groupId, group)
          }

          event.referenceGroup = {
            ...Database.toServerObject(groupCache.get(groupId))
          } as GroupDoc
        })
      ).then(() => {
        if (!isMounted) return
        setEventsForGallery(events)
        setIsProcessingEvents(false)
      })

      return () => {
        isMounted = false
      }
    }, [filteredEvents, visibleAreaStart, visibleAreaEnd])

    const sortedEvents = useMemo(() => {
      return [...eventsForGallery].sort((firstEvent, secondEvent) => {
        const firstStart = firstEvent.StartDate
        const secondStart = secondEvent.StartDate

        const firstIsNullStart =
          isTrulyEmpty(firstStart) || firstStart === 'null'
        const secondIsNullStart =
          isTrulyEmpty(secondStart) || secondStart === 'null'

        if (firstIsNullStart !== secondIsNullStart) {
          return firstIsNullStart ? -1 : 1
        }

        if (firstStart === secondStart) {
          return 0
        }

        const firstStartNumber = Number(firstStart)
        const secondStartNumber = Number(secondStart)

        if (
          Number.isFinite(firstStartNumber) &&
          Number.isFinite(secondStartNumber)
        ) {
          return firstStartNumber - secondStartNumber
        }

        return String(firstStart).localeCompare(String(secondStart))
      })
    }, [eventsForGallery])

    if (isProcessingEvents || props.loadingEvents) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          p={4}
        >
          <Typography color="text.secondary">{t('loading')}</Typography>
        </Box>
      )
    }

    if (sortedEvents.length === 0) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          p={4}
        >
          <Typography color="text.secondary">No events to display</Typography>
        </Box>
      )
    }

    return (
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          padding: '24px'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH}px, ${CARD_WIDTH}px))`,
            columnGap: `${MIN_GAP}px`,
            rowGap: `${MIN_GAP}px`,
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          {sortedEvents.map((event) => (
            <MemoizedGalleryCard
              key={`${event._id ?? event.local_id ?? ''}_${event.StartDate}`}
              event={event}
              showGroupTag={props.showGroupTag}
              embedMode={props.embedMode}
              account={props.account}
              userSettings={props.userSettings}
            />
          ))}
        </Box>
      </Box>
    )
  }
)

GalleryView.displayName = 'GalleryView'

interface GalleryCardProps {
  event: EventDoc
  showGroupTag: boolean
  embedMode?: boolean
  account?: AccountDoc
  userSettings?: UserSettingsDoc
}

function GalleryCard(props: GalleryCardProps) {
  const { event, showGroupTag } = props
  const { openPopover } = useEventPopover()
  const cardRef = React.useRef<HTMLDivElement>(null)

  const group = event.referenceGroup as GroupDoc | undefined

  const coverImage = getCoverImageNote(event.Notes)?.FileBytes
  const groupPhoto = group?.Photo || defaultGroupImage(group)

  const eventTitle = provideEventTitle(event)
  const groupName = group?.Name ?? ''

  const timeRowParts = formatGalleryTimeRowParts(event)
  const locationText = sanitizeLocation(
    event.Location?.Address ?? event.Location?.FilteredLocation ?? ''
  )

  const instanceStart = Number(event.StartDate ?? 0) * 1000
  const instanceEnd = Number(event.EndDate ?? 0) * 1000

  useEffect(() => {
    const shouldAutoOpen =
      (LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) !== null ||
        isTrulyEmpty(event._id)) &&
      isTrulyEmpty(event._rev) &&
      event._rev !== 'copy' &&
      LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) !== '1' &&
      LocalStorage.get(LocalStorageKey.DRAGGING) !== '1'

    if (!shouldAutoOpen || !cardRef.current) return

    const timeoutId = window.setTimeout(() => {
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
      cardRef.current?.click()
    }, 50)

    return () => window.clearTimeout(timeoutId)
  }, [event._id, event._rev, instanceStart, instanceEnd])

  const handleClick = () => {
    if (cardRef.current) {
      const eventId =
        eventIdForContainer(event, instanceStart) ?? event.id ?? ''
      openPopover(
        eventId,
        <EventPopupContent
          open={false}
          awayClickTriggered={false}
          setAwayClickTriggered={() => {}}
          event={event}
          group={group}
          showSmallUI={isGuest() || (props.embedMode ?? false)}
          userSettings={props.userSettings!}
          account={props.account!}
          onClose={() => {}}
          instanceStart={instanceStart}
          instanceEnd={instanceEnd}
          fc_id={eventId}
        />,
        false,
        undefined,
        cardRef.current
      )
    }
  }

  return (
    <Box
      ref={cardRef}
      onClick={handleClick}
      sx={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#fff',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      {/* Image area */}
      <Box
        sx={{
          width: '100%',
          height: IMAGE_HEIGHT,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          flexShrink: 0
        }}
      >
        {coverImage ? (
          <EventCoverImage notes={event.Notes} />
        ) : (
          <Box
            component="img"
            src={groupPhoto}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top center',
              padding: 0
            }}
          />
        )}

        {showGroupTag && groupName && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: '6px',
              padding: '3px 8px',
              maxWidth: 'calc(100% - 16px)'
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {groupName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content area */}
      <Box
        sx={{
          flex: 1,
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          overflow: 'hidden'
        }}
      >
        {/* Time row */}
        <Typography
          component="div"
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#64748B',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textTransform: 'uppercase'
          }}
        >
          {timeRowParts.map((part, index) => (
            <Box
              key={`${part.text}_${index}`}
              component="span"
              sx={{ color: part.color, fontWeight: part.fontWeight }}
            >
              {part.text}
            </Box>
          ))}
        </Typography>

        {/* Title */}
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#0F172A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3
          }}
        >
          {eventTitle}
        </Typography>

        {/* Location */}
        {locationText && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '4px',
              gap: '8px',
              '& svg': {
                width: '14px',
                height: '14px',
                color: theme.palette.text.secondary,
                flexShrink: 0
              }
            }}
          >
            <LocationIcon />
            <Typography
              sx={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {locationText}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const MemoizedGalleryCard = React.memo(GalleryCard, (prevProps, nextProps) => {
  return (
    prevProps.showGroupTag === nextProps.showGroupTag &&
    prevProps.embedMode === nextProps.embedMode &&
    prevProps.account === nextProps.account &&
    prevProps.userSettings === nextProps.userSettings &&
    prevProps.event._id === nextProps.event._id &&
    prevProps.event._rev === nextProps.event._rev &&
    prevProps.event.StartDate === nextProps.event.StartDate &&
    prevProps.event.EndDate === nextProps.event.EndDate &&
    prevProps.event.Text === nextProps.event.Text
  )
})

function sanitizeLocation(location: string): string {
  if (isTrulyEmpty(location)) return ''
  return location.toLowerCase() === 'null' ? '' : location
}

interface GalleryTimeRowPart {
  text: string
  color: string
  fontWeight?: number
}

/**
 * Formats the time row for a gallery card.
 * Handles: same day with time, same day all-day, multi-day all-day, multi-day with time.
 */
function formatGalleryTimeRowParts(event: EventDoc): GalleryTimeRowPart[] {
  const isAllDay = event.AllDay === '1'
  const tz = isAllDay
    ? 'UTC'
    : (event.TimeZoneNameID?.length ?? 0) > 0
      ? getSupportedTimezone(event.TimeZoneNameID)
      : getDefaultTimezone()

  const start = isAllDay
    ? dayjs
        .utc(Number(event.StartDate) * 1000)
        .add(13, 'hour')
        .startOf('day')
    : dayjs(Number(event.StartDate) * 1000).tz(tz)
  const end = isAllDay
    ? dayjs
        .utc(Number(event.EndDate) * 1000)
        .add(13, 'hour')
        .startOf('day')
    : dayjs(Number(event.EndDate) * 1000).tz(tz)

  const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')
  const startDateStr = start.format('MMM D').toUpperCase()
  const endDateStr = end.format('MMM D').toUpperCase()
  const primaryColor = theme.palette.primary.main
  const textColor = '#64748B'

  if (isAllDay) {
    if (sameDay) {
      return [
        { text: startDateStr, color: primaryColor, fontWeight: 600 },
        { text: '  ·  ', color: textColor, fontWeight: 500 },
        { text: t('allDay').toUpperCase(), color: textColor, fontWeight: 500 }
      ]
    }

    if (start.month() === end.month()) {
      return [
        {
          text: `${start.format('MMM').toUpperCase()} ${start.format('D')} - ${end.format('D')}`,
          color: primaryColor,
          fontWeight: 600
        },
        { text: '  ·  ', color: textColor, fontWeight: 500 },
        { text: t('allDay').toUpperCase(), color: textColor, fontWeight: 500 }
      ]
    }

    return [
      {
        text: `${startDateStr} - ${endDateStr}`,
        color: primaryColor,
        fontWeight: 600
      },
      { text: '  ·  ', color: textColor, fontWeight: 500 },
      { text: t('allDay').toUpperCase(), color: textColor, fontWeight: 500 }
    ]
  }

  const startTime = dateToTimeString(start.toDate(), true)
  const endTime = dateToTimeString(end.toDate(), true)

  if (sameDay) {
    return [
      { text: startDateStr, color: primaryColor, fontWeight: 600 },
      { text: '  ·  ', color: textColor, fontWeight: 500 },
      { text: `${startTime} - ${endTime}`, color: textColor, fontWeight: 500 }
    ]
  }

  return [
    {
      text: `${start.format('MMM D').toUpperCase()}, ${startTime} - ${end.format('MMM D').toUpperCase()}, ${endTime}`,
      color: primaryColor,
      fontWeight: 600
    }
  ]
}

export default GalleryView
